// Dependencies
import React from 'react';
import radium from 'radium';
import {connect} from 'react-redux';
import LeoInput from '../../../components/form/LeoInput.jsx';
import Button from '../../../components/button/Button.tsx';
import { setPlUuidCodeMap }  from '../../../redux/accessCodesReducer';
import { setCodes, setCodeUsagesPlUuidMap } from '../../../redux/sponsorCodeReducer';
import {ThemeContext} from '../../../components/theme/Theme.jsx';

import './AccessCodeForm.scss';
import Messages from '../../../components/messages/Messages';
import accessCodesApi from '../../../lib/api/accessCodes.js';
import sponsorApi from "../../../lib/api/sponsors";
import formService from "../../../lib/form-service";
import { FocusTrap } from "focus-trap-react";


function handleAccessCodeErrors(priceLevelLookupResponse) {
	const errMsgs = [];
	if (!priceLevelLookupResponse.valid) {
		errMsgs.push({text: 'Dieser Code ist ungültig.', type: 'errors'});
		return errMsgs;
	}
	if (!priceLevelLookupResponse.unused) {
		errMsgs.push({text: 'Dieser Code wurde bereits eingelöst.', type: 'errors'});
	}
	return errMsgs;
}

function handleSponsorCodeErrors(priceLevelCodeUsagesLookupResponse) {
	const errMsgs = [];
	if (!priceLevelCodeUsagesLookupResponse.valid) {
		errMsgs.push({text: 'Dieser Code ist ungültig.', type: 'errors'});
		return errMsgs;
	}
	return errMsgs;
}

function generatePlUuidCodeMap(priceLevelUuid, plUuidCodeMap, accessCode) {
	const resultMap = plUuidCodeMap || {};
	if (priceLevelUuid) {
		resultMap[priceLevelUuid] = accessCode;
	}
	return resultMap;
}

function generateCodeUsagePlUuIdsMap(codeUsagePlUuIdsMap, newCodeUsagePlUuIdsMap ) {
	const resultMap = codeUsagePlUuIdsMap || {};
	if (newCodeUsagePlUuIdsMap) {
		Object.entries(newCodeUsagePlUuIdsMap).map(([key, value]) => (
			resultMap[key] = value
		))
	}
	return resultMap;
}

function generateCodesList(codeList, codeString) {
	return codeList.filter(f => f !== codeString).concat([codeString])
}

class AccessCodeForm extends React.Component {
	static contextType = ThemeContext;
	constructor(props) {
		super(props);

		this.state = {
			code: '',
			isAccessCode: true,
			msgs: [],
		};

		this.handleCodeChange = this.handleCodeChange.bind(this);
	}

	handleCodeChange(e) {
		const {value} = e.target;
		let newAccessCode = value.toUpperCase();
		newAccessCode = formService.removeWhiteSpaces(newAccessCode);
		const isAccessCode = /^[0-9]/.test(newAccessCode);
		const separatorIndices = [4, 9]
		if (isAccessCode) {
			if (newAccessCode.length > 14) {
				return;
			}
			separatorIndices.forEach(i => {
				if (newAccessCode.length > i && newAccessCode[i] !== '-') {
					newAccessCode = newAccessCode.substring(0, i) + '-' + newAccessCode.substring(i, newAccessCode.length);
				}
			})
		}
		this.setState(Object.assign({}, this.state, {code: newAccessCode, isAccessCode}));
	}

	async applyCode(e) {
		e.preventDefault(); // stops browser from trying to actually submit the form.
		const codeString = this.state.isAccessCode ? this.state.code.replaceAll('-', '') : this.state.code;
		if(this.state.isAccessCode){
			try {
				const priceLevelLookupResponse = await accessCodesApi.lookupPriceLevel(
					this.props.eventUuid, codeString);
				const priceLevelUuid = priceLevelLookupResponse.priceLevelUuid;
				const plUuidCodeMap = Object.assign({}, this.props.codeMap);
				const errors = handleAccessCodeErrors(priceLevelLookupResponse);
				if (errors.length > 0) {
					this.setState({msgs: errors});
				} else {
					const map = generatePlUuidCodeMap(priceLevelUuid, plUuidCodeMap, codeString);
					this.props.setCodeMap(map);
					// If there was no error we show a success message.
					this.setState({msgs: [{text: 'Der Code wurde erfolgreich angwendet.', type: 'success'}]});
					// After the timeout we "close" the "form".
					setTimeout(() => this.props.onLeave(), 2000);
				}
			} catch (ex) {
				console.info('could not lookup pricelevel for access code', ex);
				this.setState({msgs: [{text: 'Es ist ein Fehler aufgetreten.', type: 'errors'}]});
			}
		} else {
			try {
				const priceLevelCodeUsagesLookupResponse = await sponsorApi.lookupCodeUsagesAndPriceLevel(this.props.merchantId, this.props.themeName, codeString);
				const plUuidCodeUsageMap = Object.assign({}, this.props.sponsorCodeUsagesMap);
				const codeList = Object.assign([], this.props.sponsorCodes);

				const errors = handleSponsorCodeErrors(priceLevelCodeUsagesLookupResponse);
				if (errors.length > 0) {
					this.setState({msgs: errors});
				} else {
					const map = generateCodeUsagePlUuIdsMap(plUuidCodeUsageMap, priceLevelCodeUsagesLookupResponse.codeUsagesPlUuid);
					const codes = generateCodesList(codeList, codeString);
					this.props.setCodes(codes)
					this.props.setCodeUsageMap(map);
					// If there was no error we show a success message.
					this.setState({msgs: [{text: 'Der Code wurde erfolgreich angwendet.', type: 'success'}]});
					// After the timeout we "close" the "form".
					setTimeout(() => this.props.onLeave(), 2000);
				}
			} catch (e) {
				console.info('could not lookup pricelevel and code usages for sponsor code', e);
				this.setState({msgs: [{text: 'Es ist ein Fehler aufgetreten.', type: 'errors'}]});
			}
		}

	}

	render() {
		const text = this.context.themeManager.getThemeTextOrHardcodedDefault('accessCodeInfoText');
		const btnText = this.context.themeManager.getThemeTextOrHardcodedDefault('accessCodeBtnText');
		return (
			<FocusTrap active={true}
					   focusTrapOptions={{
						   clickOutsideDeactivates: false,
						   allowOutsideClick: true,
						   preventScroll: false
					   }}>
			<div>
				<Messages
					msgs={this.state.msgs}
				/>
				<div className="access-code__container">
					<h3 className="access-code__text">{text}</h3>
					<form onSubmit={(e) => this.applyCode(e)}>
						<div className="access-code__form">
							<div className="access-code__inputs-container">
								<div className="access-code__form-element">
									<LeoInput
										type="text"
										maxLength="40"
										value={this.state.code}
										onChange={(e) => this.handleCodeChange(e)}
										autoComplete="off"
										id="code"
										name="Code"
										title="Code eingeben"
										label="Code"
									/>
								</div>
							</div>
						</div>
						<div className="button-line">
							{!this.props.codeMandatory && <Button
								name="cancel"
								buttonText="Abbrechen"
								onClick={() => this.props.onLeave()}
								btnStyle="highlight"
							/>}
							<Button
								name="redeemAccessCode"
								buttonText={btnText}
								type={'submit'}
								btnStyle="highlight"
								disabled={!this.state.code}
							/>
						</div>
					</form>
				</div>
			</div>
			</FocusTrap>
		);
	}
}

const RadiumAccessCodeForm = radium(AccessCodeForm);

// ------------------------------
// Store Connection
// ------------------------------
const mapStateToProps = (state) => ({
	merchantId: state.merchant.merchantId,
	themeName: state.merchant.themeName,
	eventUuid: state.event.selected,
	codeMap: state.accessCodes.plUuidCodeMap,
	sponsorCodeUsagesMap: state.sponsorCodes.codeUsagesPlUuidMap,
	sponsorCodes: state.sponsorCodes.sponsorCodes
});

const mapDispatchToProps = (dispatch) => ({
	setCodeMap: (map) => {
			dispatch(setPlUuidCodeMap(map))},
	setCodeUsageMap: (map) => {
			dispatch(setCodeUsagesPlUuidMap(map))},
	setCodes: (codeList) => {
		dispatch(setCodes(codeList))
	}
});

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(RadiumAccessCodeForm);
